import { useCallback, useEffect, useState } from 'react'
import { api } from '../api'
import {
  Badge,
  Empty,
  Modal,
  Page,
  Panel,
  StatTile,
  Table,
  TileRow,
  fmtDateTime,
  fmtDuration,
  fmtNumber,
  fmtPhone,
} from '../components/ui'
import type { ActiveCall, CallLogPage, CallTimeline, CampaignSummary, UserRow } from '../types'

const today = () => new Date().toISOString().slice(0, 10)

export default function Calls() {
  const [from, setFrom] = useState(today())
  const [to, setTo] = useState(today())
  const [campaignId, setCampaignId] = useState('')
  const [agentId, setAgentId] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)

  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([])
  const [users, setUsers] = useState<UserRow[]>([])
  const [data, setData] = useState<CallLogPage | null>(null)
  const [active, setActive] = useState<ActiveCall[]>([])
  const [loading, setLoading] = useState(true)
  const [recording, setRecording] = useState<{ callId: string } | null>(null)
  const [timeline, setTimeline] = useState<{ callId: string } | null>(null)

  useEffect(() => {
    api.campaigns().then(setCampaigns).catch(() => setCampaigns([]))
    api.users().then((u) => setUsers(u.filter((x) => x.role === 'agent'))).catch(() => setUsers([]))
  }, [])

  useEffect(() => {
    const poll = () => api.activeCalls().then(setActive).catch(() => setActive([]))
    poll()
    const t = setInterval(poll, 4000)
    return () => clearInterval(t)
  }, [])

  const load = useCallback(() => {
    setLoading(true)
    api
      .callLog({
        from,
        to,
        campaign_id: campaignId || undefined,
        agent_id: agentId || undefined,
        q: q || undefined,
        page,
      })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [from, to, campaignId, agentId, q, page])

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0)
    return () => clearTimeout(t)
  }, [load, q])

  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / 50))

  return (
    <Page title="Calls" subtitle="Everything that has been dialed, and what is on the wire now">
      <TileRow>
        <StatTile label="On the wire" value={active.length} tone="live" />
        <StatTile label="In range" value={fmtNumber(total)} />
        <StatTile
          label="Abandoned"
          value={fmtNumber(data?.calls.filter((c) => c.abandoned).length ?? 0)}
          sub="on this page"
        />
      </TileRow>

      {active.length > 0 && (
        <Panel title="Live now">
          <Table head={['Started', 'Number', 'Campaign', 'Agent', 'State']}>
            {active.map((c) => (
              <tr key={c.id}>
                <td className="muted">{fmtDateTime(c.started_at)}</td>
                <td className="mono strong">{fmtPhone(c.number)}</td>
                <td>{c.campaign ?? '—'}</td>
                <td>{c.agent ?? '—'}</td>
                <td>
                  <Badge tone={c.state === 'talking' ? 'live' : 'warn'}>{c.state}</Badge>
                </td>
              </tr>
            ))}
          </Table>
        </Panel>
      )}

      <Panel title="Call log">
        <div className="filter-bar">
          <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(0) }} />
          <span className="muted">to</span>
          <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(0) }} />

          <select value={campaignId} onChange={(e) => { setCampaignId(e.target.value); setPage(0) }}>
            <option value="">All campaigns</option>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select value={agentId} onChange={(e) => { setAgentId(e.target.value); setPage(0) }}>
            <option value="">All agents</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>

          <input
            className="grow"
            placeholder="Search by number"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0) }}
          />
        </div>

        {loading && !data ? (
          <p className="muted">Loading…</p>
        ) : !data || data.calls.length === 0 ? (
          <Empty
            message="No calls in this range."
            hint="Widen the dates, or check that a campaign is running."
          />
        ) : (
          <>
            <Table
              head={['When', 'Number', 'Caller ID', 'Campaign', 'Agent', 'Duration', 'Talk', 'Disposition', 'Result', '']}
            >
              {data.calls.map((c) => (
                <tr key={c.id} className={c.abandoned ? 'row-danger' : ''}>
                  <td className="muted">{fmtDateTime(c.at)}</td>
                  <td className="mono strong">{fmtPhone(c.number)}</td>
                  <td className="mono muted">{c.caller_id ? fmtPhone(c.caller_id) : '—'}</td>
                  <td>{c.campaign ?? '—'}</td>
                  <td>{c.agent ?? <span className="muted">—</span>}</td>
                  <td className="mono">{fmtDuration(c.duration_sec)}</td>
                  <td className="mono">{fmtDuration(c.talk_sec)}</td>
                  <td>{c.disposition ?? <span className="muted">—</span>}</td>
                  <td>
                    {c.abandoned ? (
                      <Badge tone="danger">abandoned</Badge>
                    ) : c.amd_result ? (
                      <Badge tone="muted">{c.amd_result}</Badge>
                    ) : (
                      <span className="muted small">{c.hangup_cause ?? '—'}</span>
                    )}
                  </td>
                  <td className="row-actions">
                    <button className="link" onClick={() => setTimeline({ callId: c.id })}>
                      Timeline
                    </button>
                    {c.recording_id && (
                      <button className="link" onClick={() => setRecording({ callId: c.id })}>
                        Recording
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>

            <div className="pager">
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Previous</button>
              <span className="muted">
                Page {page + 1} of {fmtNumber(pages)} · {fmtNumber(total)} calls
              </span>
              <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>Next →</button>
            </div>
          </>
        )}
      </Panel>

      {recording && (
        <RecordingModal callId={recording.callId} onClose={() => setRecording(null)} />
      )}
      {timeline && (
        <TimelineModal callId={timeline.callId} onClose={() => setTimeline(null)} />
      )}
    </Page>
  )
}

function RecordingModal({ callId, onClose }: { callId: string; onClose: () => void }) {
  const [meta, setMeta] = useState<{
    sha256: string | null
    bytes: number | null
    path: string
    verified: boolean
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.recording(callId).then(setMeta).catch((e) => setError(e.message))
  }, [callId])

  return (
    <Modal title="Recording" onClose={onClose}>
      {error && <p className="error">{error}</p>}
      {!meta ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          <dl className="kv">
            <div><dt>File</dt><dd className="mono small">{meta.path}</dd></div>
            <div><dt>Size</dt><dd>{meta.bytes ? `${(meta.bytes / 1024).toFixed(0)} KB` : '—'}</dd></div>
            <div>
              <dt>Integrity</dt>
              <dd>
                {meta.verified ? (
                  <Badge tone="ok">hashed at capture</Badge>
                ) : (
                  <Badge tone="warn">not hashed</Badge>
                )}
              </dd>
            </div>
          </dl>

          {meta.sha256 && (
            <>
              <p className="muted small">
                SHA-256 taken the moment recording finished. A file that still matches this has not
                been altered since capture.
              </p>
              <pre className="code-block hash">{meta.sha256}</pre>
            </>
          )}

          <p className="muted small">
            Playback in the browser arrives with the recording service; the file is on the media
            server at the path above.
          </p>
        </>
      )}
      <div className="modal-actions">
        <button className="primary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  )
}

/**
 * The whole life of one call, in order.
 *
 * A dispute about a call — "nobody was there", "your agent hung up on me",
 * "you called me at 7am" — is settled by this screen or not at all. Everything
 * on it comes from records written while the call was happening: the call
 * row's own timestamps, the event stream, the compliance decision that let it
 * be dialled, and the recording hash.
 */
function TimelineModal({ callId, onClose }: { callId: string; onClose: () => void }) {
  const [data, setData] = useState<CallTimeline | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.callTimeline(callId).then(setData).catch((e) => setError(String(e)))
  }, [callId])

  const KIND_LABEL: Record<string, string> = {
    initiated: 'Dialled',
    answered: 'Answered',
    bridged: 'Connected',
    ended: 'Ended',
  }

  return (
    <Modal title="Call timeline" onClose={onClose} wide>
      {error && <p className="error">{error}</p>}
      {!data ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          <dl className="kv">
            <div><dt>Number</dt><dd className="mono">{fmtPhone(data.call.dialed_number)}</dd></div>
            <div><dt>Campaign</dt><dd>{data.call.campaign ?? '—'}</dd></div>
            <div><dt>Agent</dt><dd>{data.call.agent ?? '—'}</dd></div>
            <div><dt>Mode</dt><dd>{data.call.dial_mode ?? data.call.direction}</dd></div>
            {data.call.abandoned && (
              <div><dt>Outcome</dt><dd><Badge tone="danger">abandoned</Badge></dd></div>
            )}
            {data.disposition && (
              <div>
                <dt>Disposition</dt>
                <dd>
                  {data.disposition.label}
                  {data.disposition.note && (
                    <span className="muted small"> — {data.disposition.note}</span>
                  )}
                </dd>
              </div>
            )}
          </dl>

          <div className="timeline">
            {data.timeline.map((t, i) => (
              <div key={i} className={`timeline-row ${t.kind}`}>
                <span className="timeline-at mono">{fmtDateTime(t.at)}</span>
                <span className="timeline-dot" aria-hidden />
                <span className="timeline-body">
                  <span className="strong">{KIND_LABEL[t.kind] ?? t.kind.replace(/_/g, ' ')}</span>
                  {t.summary && <span className="muted"> — {t.summary}</span>}
                </span>
              </div>
            ))}
          </div>

          {data.compliance.length > 0 && (
            <>
              <h3 className="section-title">Compliance record</h3>
              <p className="muted small">
                Every decision the gate made about this number around the time of the call.
                Rows for this call are highlighted; the others are context — the same number
                being blocked or deferred before or after.
              </p>
              <Table head={['When', 'Decision', 'Reason', '']}>
                {data.compliance.map((d, i) => (
                  <tr key={i} className={d.this_call ? 'row-selected' : undefined}>
                    <td className="mono">{fmtDateTime(d.at)}</td>
                    <td>
                      <Badge tone={d.decision === 'allow' ? 'ok' : d.decision === 'defer' ? 'warn' : 'danger'}>
                        {d.decision}
                      </Badge>
                    </td>
                    <td className="mono small">{d.reason_code ?? '—'}</td>
                    <td className="muted small">{d.this_call ? 'this call' : ''}</td>
                  </tr>
                ))}
              </Table>
            </>
          )}

          {data.recording && (
            <p className="muted small">
              Recorded to <span className="mono">{data.recording.path}</span>
              {data.recording.sha256 && ' · SHA-256 taken at capture'}
            </p>
          )}
        </>
      )}
      <div className="modal-actions">
        <button className="primary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  )
}
